/**
 * Browser console logs for filter transform debugging.
 * Set REACT_APP_FILTER_MASK_DEBUG=false to disable.
 * Server-side logs use map_provider_server config filter_mask_debug.
 */
export function isFilterMaskDebugEnabled(): boolean {
    return process.env.REACT_APP_FILTER_MASK_DEBUG !== "false";
}

export type FilterMaskDebugPayload = Record<string, unknown>;

export function logFilterMaskDebug(event: string, payload: FilterMaskDebugPayload): void {
    if (!isFilterMaskDebugEnabled()) {
        return;
    }
    console.info("[filter-mask]", event, payload);
}

export type LiveMapMetadataSnapshot = {
    width?: number;
    height?: number;
    resolution?: number;
    origin?: { x?: number; y?: number };
    originYaw?: number;
};

export type GridSizeCells = {
    width: number;
    height: number;
};

export type FilterMaskGridSizeContext = {
    event: string;
    /** Live `/map_metadata` grid (Nav2 `/map` static layer expects this geometry). */
    liveMapGrid?: GridSizeCells | null;
    /** Variant / mask raster natural pixel size (becomes filter_mask_server image dimensions). */
    variantImagePx?: GridSizeCells | null;
    /** Post-materialize mask image size when returned by map_provider_server debug. */
    materializedMaskPx?: GridSizeCells | null;
    extra?: FilterMaskDebugPayload;
};

function occupancyGridDataLength(cells: GridSizeCells): number {
    return cells.width * cells.height;
}

function parsePxPairFromDebug(obj: unknown): GridSizeCells | null {
    if (!obj || typeof obj !== "object") {
        return null;
    }
    const o = obj as Record<string, unknown>;
    const w = o.width_px ?? o.width;
    const h = o.height_px ?? o.height;
    if (
        typeof w === "number" &&
        typeof h === "number" &&
        Number.isFinite(w) &&
        Number.isFinite(h) &&
        w > 0 &&
        h > 0
    ) {
        return { width: Math.round(w), height: Math.round(h) };
    }
    return null;
}

/** Pull width/height pairs from map_provider_server materialize `debug` payload. */
export function gridSizesFromMaterializeDebug(
    debug: FilterMaskDebugPayload | undefined,
): {
    variantImagePx: GridSizeCells | null;
    materializedMaskPx: GridSizeCells | null;
    base2dImagePx: GridSizeCells | null;
} {
    if (!debug) {
        return { variantImagePx: null, materializedMaskPx: null, base2dImagePx: null };
    }
    const targetGrid = parsePxPairFromDebug(debug.target_grid_cells);
    return {
        variantImagePx: parsePxPairFromDebug(debug.variant_image_px),
        materializedMaskPx:
            parsePxPairFromDebug(debug.materialized_image_px) ??
            parsePxPairFromDebug(debug.mask_image_px) ??
            parsePxPairFromDebug(debug.output_image_px) ??
            targetGrid,
        base2dImagePx: parsePxPairFromDebug(debug.base_2d_image_px),
    };
}

/**
 * Console summary of live map vs keep-out mask grid sizes.
 * Logs `console.error` when widths/heights disagree (common cause of misaligned or
 * malformed Nav2 OccupancyGrid on `/keepout_filter_mask` vs `/map`).
 */
export function logFilterMaskGridSizeContext(ctx: FilterMaskGridSizeContext): void {
    if (!isFilterMaskDebugEnabled()) {
        return;
    }

    const { event, liveMapGrid, variantImagePx, materializedMaskPx, extra } = ctx;
    const grids: Array<{
        label: string;
        width: number;
        height: number;
        expected_data_length: number;
    }> = [];

    const pushGrid = (label: string, g: GridSizeCells | null | undefined) => {
        if (!g) {
            return;
        }
        grids.push({
            label,
            width: g.width,
            height: g.height,
            expected_data_length: occupancyGridDataLength(g),
        });
    };

    pushGrid("live /map_metadata (cells)", liveMapGrid);
    pushGrid("variant / mask image (px)", variantImagePx);
    pushGrid("materialized mask (px)", materializedMaskPx);

    const mismatches: string[] = [];
    const comparePair = (
        a: GridSizeCells | null | undefined,
        aLabel: string,
        b: GridSizeCells | null | undefined,
        bLabel: string,
    ) => {
        if (!a || !b) {
            return;
        }
        if (a.width !== b.width || a.height !== b.height) {
            mismatches.push(
                `${aLabel} ${a.width}×${a.height} ≠ ${bLabel} ${b.width}×${b.height} ` +
                    `(data length ${occupancyGridDataLength(a)} vs ${occupancyGridDataLength(b)})`,
            );
        }
    };

    comparePair(liveMapGrid, "live map", variantImagePx, "variant image");
    comparePair(liveMapGrid, "live map", materializedMaskPx, "materialized mask");
    comparePair(variantImagePx, "variant image", materializedMaskPx, "materialized mask");

    const summary: FilterMaskDebugPayload = {
        event,
        grids,
        mismatches,
        nav2_note:
            "OccupancyGrid is malformed when data.length !== width×height; " +
            "global_costmap static_layer subscribes to /map, filter_mask_server publishes /keepout_filter_mask",
        ...extra,
    };

    if (mismatches.length > 0) {
        console.error(
            `[filter-mask] GRID SIZE MISMATCH (${event}): ${mismatches.join("; ")}`,
            summary,
        );
    } else if (grids.length >= 2) {
        const sizes = grids.map((g) => `${g.label} ${g.width}×${g.height}`).join(" | ");
        console.info(`[filter-mask] grid sizes aligned (${event}): ${sizes}`, summary);
    }
    // Skip logging when only one grid is known (e.g. before variant image fetch).
}

/** Compare webapp /map_metadata to materialize API debug block. */
export function logFilterTransformCompare(
    event: string,
    liveMeta: LiveMapMetadataSnapshot | null | undefined,
    materializeDebug: FilterMaskDebugPayload | undefined,
    extra?: FilterMaskDebugPayload,
): void {
    if (!isFilterMaskDebugEnabled() && !materializeDebug) {
        return;
    }
    const yaml = (materializeDebug?.yaml_after_rewrite ?? materializeDebug?.yaml_after_write) as
        | FilterMaskDebugPayload
        | undefined;
    const compare: FilterMaskDebugPayload = {
        live_map_metadata: liveMeta
            ? {
                  width_cells: liveMeta.width,
                  height_cells: liveMeta.height,
                  resolution: liveMeta.resolution,
                  origin_x: liveMeta.origin?.x,
                  origin_y: liveMeta.origin?.y,
                  origin_yaw: liveMeta.originYaw,
              }
            : null,
        materialize_yaml: yaml
            ? {
                  resolution: yaml.resolution,
                  origin: yaml.origin,
                  origin_x: yaml.origin_x,
                  origin_y: yaml.origin_y,
                  origin_yaw: yaml.origin_yaw,
                  image: yaml.image,
              }
            : null,
        materialize_hints: materializeDebug?.hints,
        variant_image_px: materializeDebug?.variant_image_px,
        base_2d_image_px: materializeDebug?.base_2d_image_px,
        ...extra,
    };
    if (liveMeta && yaml) {
        const hints: string[] = [];
        const lr = liveMeta.resolution;
        const yr = yaml.resolution;
        if (typeof lr === "number" && typeof yr === "number" && Math.abs(lr - yr) > 1e-6) {
            hints.push(`resolution mismatch: live=${lr} yaml=${yr}`);
        }
        const lox = liveMeta.origin?.x;
        const loy = liveMeta.origin?.y;
        const yox = yaml.origin_x ?? (Array.isArray(yaml.origin) ? yaml.origin[0] : undefined);
        const yoy = yaml.origin_y ?? (Array.isArray(yaml.origin) ? yaml.origin[1] : undefined);
        if (
            typeof lox === "number" &&
            typeof loy === "number" &&
            typeof yox === "number" &&
            typeof yoy === "number"
        ) {
            const dx = Math.abs(lox - yox);
            const dy = Math.abs(loy - yoy);
            if (dx > 1e-4 || dy > 1e-4) {
                hints.push(
                    `origin mismatch: live=[${lox}, ${loy}] yaml=[${yox}, ${yoy}] delta=[${dx}, ${dy}]`,
                );
                console.warn(
                    "[filter-mask] ORIGIN MISMATCH — filter YAML origin does not match live /map_metadata",
                    { live_origin: [lox, loy], yaml_origin: [yox, yoy], delta: [dx, dy], event },
                );
            }
        }
        const vp = materializeDebug?.variant_image_px as
            | { width_px?: number; height_px?: number }
            | undefined;
        if (
            vp?.width_px &&
            vp?.height_px &&
            liveMeta.width &&
            liveMeta.height &&
            (vp.width_px !== liveMeta.width || vp.height_px !== liveMeta.height)
        ) {
            hints.push(
                `image size mismatch: variant ${vp.width_px}x${vp.height_px}px vs ` +
                    `live grid ${liveMeta.width}x${liveMeta.height} cells`,
            );
        }
        if (hints.length) {
            compare.transform_mismatch_hints = hints;
        } else {
            compare.transform_mismatch_hints = ["live metadata and materialize YAML look aligned"];
        }
    }

    const { variantImagePx, materializedMaskPx } = gridSizesFromMaterializeDebug(materializeDebug);
    const liveGrid =
        liveMeta?.width && liveMeta?.height
            ? { width: liveMeta.width, height: liveMeta.height }
            : null;
    logFilterMaskGridSizeContext({
        event,
        liveMapGrid: liveGrid,
        variantImagePx,
        materializedMaskPx,
        extra,
    });

    logFilterMaskDebug(event, compare);
}

export type MapEditorSaveDebugPayload = {
    export_path:
        | "nav2_cell_aligned"
        | "merge_composite"
        | "variant_passthrough"
        | "nav2_from_layers"
        | "nav2_url_fetch"
        | "nav2_fallback";
    grid_cells: { width: number; height: number; source?: string };
    /** Variant file being edited (.png). */
    variant_natural_px: { width: number; height: number } | null;
    /** Companion STCM BMP (or variant when no parent) used for layout / Nav2 transform. */
    layout_natural_px: { width: number; height: number } | null;
    uses_companion_geometry: boolean;
    /** Preview stretched to live /map_metadata canvas (false = Aurora 2D export size). */
    editor_grid_fill?: boolean;
    /** Canvas sized from preview-bmp, not ros_metadata. */
    editor_aurora_2d_grid?: boolean;
    live_grid_cells?: { width: number; height: number } | null;
    variant_aspect_mismatch: boolean;
    editor_canvas_px: { width: number; height: number };
    needs_nav2_resample: boolean;
    nav2_transform: { kx: number; ky: number; tx: number; ty: number } | null;
    sketch_bounds_canvas_px: {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
        objectCount: number;
    } | null;
    output_blob_px: { width: number; height: number } | null;
    filter_geometry: {
        resolution?: number;
        origin_x?: number;
        origin_y?: number;
        origin_yaw?: number;
    } | null;
    alignment_hints: string[];
    /** Set when primary Nav2 export failed and a fallback path was used. */
    export_layer_error?: string;
    /** Raster URLs composited into the export (fetch+blob, not Fabric snapshot). */
    export_image_urls?: string[];
};

/** Console diagnostics when saving from the map editor (filter mask alignment). */
export function logMapEditorSaveDebug(payload: MapEditorSaveDebugPayload): void {
    if (!isFilterMaskDebugEnabled()) {
        return;
    }
    logFilterMaskDebug("map_editor_save", payload);
}
